import {ApiUrl, type RequestParam} from "../api";
import {DataTableContextMenu} from "./dataTableContextMenu";
import * as GPSPositionUtil from "@/shared/recording/gpsPositionUtil";
import {breadcrumbsWithoutName, htmlEscape} from "../utils/string";
import {isJQueryPromise, promiseNow} from "../utils/promise";
import {buildPlatformIconTitle, clickedInsideTextElement, mergeProperties} from "@/shared/utils/misc";
import {
	formatDate,
	formatDateDay,
	formatDateTime,
	formatDateWithoutYear,
	formatDuration,
	formatTime,
	isSameDay,
	parseISODate,
	parseISOZonedDateTime,
	Temporal
} from "@/shared/utils/datefunctions";
import {GPSPosition, PartialUser, ReportProjectTime, ReportRecord, RunningEntry, RunningProjectTime, User} from "@/shared/timr";
import {DurationUnit} from "@/shared/timrTypes";

interface TimeRangeRecord {
	start: string | null;
	end: string | null;
	editUnit?: DurationUnit;
	changed?: boolean;
}

function hasTimeRangeFields<T>(r: T): r is T & TimeRangeRecord {
	return typeof r === "object" && r !== null && "start" in (r as object) && "end" in (r as object);
}

declare let $clamp: (el: HTMLElement, opts: { clamp: number }) => { clamped: string | undefined; original: string };

$.extend(true, $.fn.dataTable.defaults, {
	autoWidth: false,
	searching: false,
	processing: true,
	language: {
		zeroRecords: messages["datatable.empty"],
		emptyTable: messages["datatable.empty"],
		info: messages["datatable.info"],
		infoEmpty: messages["datatable.infoEmpty"],
		infoFiltered: "",
		paginate: {
			first: messages["datatable.paginate.first"],
			last: messages["datatable.paginate.last"],
			next: messages["datatable.paginate.next"],
			previous: messages["datatable.paginate.previous"]
		},
		// Reuses the app's own overlay component (components/processingOverlays.less), the same one
		// the validations panel and user-count panel use, instead of DataTables' white gradient strip.
		processing: `<div class="overlay-processing-container">
									<span class="overlay-processing-text">
										<i class="icon-spinner2 spinner text-primary"></i>
										${messages["datatable.processing"]}
									</span>
								</div>`
	},
	buttons: {
		dom: {},
		buttons: []
	}
});
$.fn.dataTable.ext.errMode = "throw";

export interface BasicDataTableConfig {
	element: JQuery;
	initialFilter?: RequestParam[];
	orderable?: boolean;
	allowExpand?: boolean;
	clampNote?: boolean;
	excludeElementsDblclickEvent?: string[];
}

/**
 * Data for the BasicDataTable can be sourced in different ways:
 * - string: provide an endpoint url to request data via ajax requests
 * - array: provide static data directly using an array
 * - function, returns array: provide a function that gets called on reload that returns an array
 * - function, returns promise: provide a function that get called on reload that returns a promise of an array,
 * 		used for example to create an ajax request on every reload
 */
export type DataTablesDataSource<T> = string | T[] | (() => T[]) | (() => PromiseLike<T[]>);

/** A DataTables request column reshaped by {@link BasicDataTable.planifyAjaxData}: `search` flattened into `searchRegex`/`searchValue` for the server. */
type PlanifiedAjaxColumn = Omit<DataTables.AjaxDataRequestColumn, "search"> & {
	search?: DataTables.AjaxDataRequestSearch;
	searchRegex?: boolean;
	searchValue?: string;
};

/**
 * Two-level dot-paths through `T`, matching how DataTables resolves nested row
 * values from `data: "user.fullname"`. Depth is capped at two; arrays are not
 * descended (they appear as leaf keys).
 */
export type DotPath<T> = T extends object
	? {
		[K in Extract<keyof T, string>]: NonNullable<T[K]> extends object
			? NonNullable<T[K]> extends readonly unknown[]
				? K
				: K | `${K}.${Extract<keyof NonNullable<T[K]>, string>}`
			: K;
	}[Extract<keyof T, string>]
	: never;

/** Resolve the value type at a {@link DotPath} `P` within `T` (the type DataTables passes as `render`'s first argument). */
export type GetByPath<T, P extends string> =
	T extends unknown
		? P extends `${infer K}.${infer Rest}`
			? K extends keyof T
				? GetByPath<NonNullable<T[K]>, Rest>
				: undefined
			: P extends keyof T
				? T[P]
				: undefined
		: never;

/** The render-call phase DataTables passes as `render`'s second argument. */
export type RenderType = "display" | "filter" | "sort" | "type";

/**
 * `DataTables.ColumnSettings` with strongly-typed `row` and per-column `data`.
 * Two variants:
 * - **Data column**: `data` is a {@link DotPath} of `TRow`; render's first arg
 *   is narrowed to `GetByPath<TRow, K>`.
 * - **Virtual column**: `data` is omitted; render's first arg is `void`.
 *   Identify these via `name` for DataTables ordering/search.
 */
export type TypedColumnSettings<TRow> = Omit<DataTables.ColumnSettings, "data" | "render"> & (
	| {
	[K in DotPath<TRow>]: {
		data: K;
		render?:
			| number
			| string
			| DataTables.ObjectColumnData
			| DataTables.ObjectColumnRender
			| ((data: GetByPath<TRow, K>, type: RenderType, row: TRow, meta: DataTables.CellMetaSettings) => string | number | void);
	};
}[DotPath<TRow>]
	| {
	data?: undefined;
	render?:
		| number
		| string
		| DataTables.ObjectColumnData
		| DataTables.ObjectColumnRender
		| ((data: void, type: RenderType, row: TRow, meta: DataTables.CellMetaSettings) => string | number | void);
}
	);

/**
 * Server-side DataTables envelope (https://datatables.net/manual/server-side#Returned-data),
 * generic over the row type `T`. Used in preference to `DataTables.AjaxData` /
 * `AjaxResponse` from the upstream typings because those declare `data: any`
 * and leave required fields optional.
 */
export interface DataTableResponse<T> {
	draw: number;
	data: T[];
	recordsFiltered: number;
	recordsTotal: number;
}

/**
 * Base class for DataTables-backed grids.
 *
 * The class generic `T` is the **row type** — what `dataSrc` resolves to, what
 * `getRowData()` returns, what column renderers receive as `row`. The full ajax
 * **response** is typically a wider envelope around `T[]` (dashboards add
 * breadcrumb fields, totals, etc.) and is delivered to subscribers via
 * {@link BasicDataTable.onDataChanged} — pass that response type at the call
 * site rather than the class generic.
 *
 * Tables with bulk selection extend {@link SelectableDataTable} instead.
 */
export abstract class BasicDataTable<T> {
	public static EVENT_DATA_CHANGED = "dataChanged";
	public static EVENT_DBLCLICK = "dblclickRowElement";
	public static COLUMN_SIZE_TYPE_NAME = "250px";
	public static COLUMN_SIZE_STATUS_ICON = "18px";
	public static COLUMN_SIZE_QUICK_SWITCH_BUTTON = "32px";
	public static COLUMN_SIZE_GPS_ICON = "18px";
	public static COLUMN_SIZE_BREAKTIME = "70px";
	public static COLUMN_SIZE_DURATION = "82px";
	public static COLUMN_SIZE_DURATION_NET = "108px";
	public static COLUMN_SIZE_CHECKBOX = "24px";
	public static COLUMN_SIZE_MENU = "14px";
	public static EXCLUDE_ROW_CLASS_DBLCLICK = "exclude-row-dblclick";

	public static MAX_DATE_WIDTH = 90;
	public static ARROW_WIDTH = 20;
	public static TIMEZONE_FIELD_WIDTH = 36;
	public static DAYSUNIT_FIELD_WIDTH = 100;
	public static TIME_24_WIDTH = 35;
	public static TIME_12_WIDTH = 57;
	public static CHANGED_WIDTH = 20;

	protected dataTable: DataTables.Api;
	protected dataSource: DataTablesDataSource<T>;
	protected filterData: RequestParam[];
	protected contextMenu: DataTableContextMenu<T>;
	private initCompleteDeferred: JQueryDeferred<void>;

	constructor(dataSource: DataTablesDataSource<T>, public config: BasicDataTableConfig) {
		this.dataSource = dataSource;
		this.filterData = config.initialFilter || [];
		this.contextMenu = this.createContextMenu();
		this.dataTable = this.config.element.DataTable(mergeProperties(
			this.createBasicConfig(),
			this.createDataTableConfig()));
		this.config.element.addClass(this.config.orderable ? "table-orderable" : "table-not-orderable");

		this.dataTable.on("xhr", (e, settings, json, xhr: XMLHttpRequest) => {
			if (xhr.status === 200) {
				if (ApiUrl.isLoginPage(xhr)) {
					window.location.href = "/timr/";
				}
				$(this).triggerHandler(BasicDataTable.EVENT_DATA_CHANGED, [json]);
			}
		});

		this.dataTable.on("user-select", (event, _dt: DataTables.Api, type: string,
			_cell: DataTables.CellMethods, mouseEvent: JQuery.TriggeredEvent) => {
			// Text clicks belong to native text selection, leaving row selection unchanged.
			if (type === "row" && clickedInsideTextElement(mouseEvent.target as Element, mouseEvent.clientX, mouseEvent.clientY)) {
				event.preventDefault();
			}
		});

		this.config.element.on("mousedown dblclick", `tbody > tr:not(tr.${BasicDataTable.EXCLUDE_ROW_CLASS_DBLCLICK})`, (e) => {
			if (e.type === "mousedown" && e.detail < 2) return;

			const target = $(e.target as HTMLElement);
			let excludeElements = `ul, li, a, button, input, textarea, select, [contenteditable]:not([contenteditable="false"])`;
			if (this.config.excludeElementsDblclickEvent) {
				this.config.excludeElementsDblclickEvent.forEach((element) => {
					excludeElements += ", " + element;
				});
			}
			if (target.closest(excludeElements).length === 0 && !clickedInsideTextElement(e.target as Element, e.clientX, e.clientY)) {
				if (e.type === "mousedown") {
					// Text selection starts on the second mousedown, before dblclick can prevent it.
					e.preventDefault();
					return;
				}
				const tr = target.closest("tr");
				const rowData = this.getRowData(tr);
				$(this).triggerHandler(BasicDataTable.EVENT_DBLCLICK, [rowData, e]);
			}
		});

		if (this.config.clampNote) {
			this.dataTable.on("draw.dt", () => {
				$("td.table-data-description > div")
					.toArray()
					.filter(ele => ele.innerText !== "")
					.forEach((ele: HTMLElement) => {
						const clampResult = $clamp(ele, {
							clamp: 2
						});

						const jqueryElement = $(ele);
						const jqueryElementTd = $(ele).closest("td.table-data-description");

						const isClampedByJS = (clampResult.clamped !== undefined) && (clampResult.clamped !== clampResult.original);
						const isClampedByCSS = ele.scrollHeight > jqueryElement.innerHeight();
						jqueryElement.attr("data-clamped", "true");
						if (this.config.allowExpand) {
							if (isClampedByJS || isClampedByCSS) {
								jqueryElementTd.attr("data-clamped-expanded", "false");
							}
							if (isClampedByCSS) {
								jqueryElementTd.attr("data-clamped-type", "css");
							} else if (isClampedByJS) {
								// first replace is to show correct linebreaks
								// second replace is to cut out line breaks, which are at the end of the text
								// TIMR-4162 for more information
								const clampResultLineBreakReplaced = clampResult.original !== ""
									? clampResult.original.replace(/&lt;br\s*\/&gt;/g, "<br/>").replace(/^(\s*<br\s*\/?>)*\s*|\s*(<br\s*\/?>\s*)*$/g, "")
									: "";
								jqueryElementTd.attr("data-clamped-type", "js");
								$(`<div style="display: none"></div>`).html(clampResultLineBreakReplaced).insertAfter(jqueryElement.closest("td.table-data-description > div"));
							}
						}
					});
			});
		}
		if (this.config.allowExpand) {
			this.dataTable.on("click", (e) => {
				const td = $(e.target as HTMLElement).closest("td.table-data-description");
				const tdDiv = $(e.target as HTMLElement).closest("td.table-data-description > div");
				if (window.getSelection().toString() === "") {
					if (td.attr("data-clamped-type") === "css") {
						if (td.attr("data-clamped-expanded") === "false") {
							tdDiv.css("-webkit-line-clamp", "unset");
							td.attr("data-clamped-expanded", "true");
						} else {
							tdDiv.css("-webkit-line-clamp", "2");
							td.attr("data-clamped-expanded", "false");
						}
					}
					if (td.attr("data-clamped-type") === "js") {
						if (td.attr("data-clamped-expanded") === "false") {
							td.find(":nth-child(1)").hide();
							td.find(":nth-child(2)").show();
							td.attr("data-clamped-expanded", "true");
						} else {
							td.find(":nth-child(1)").show();
							td.find(":nth-child(2)").hide();
							td.attr("data-clamped-expanded", "false");
						}
					}
				}
			});
		}

		this.onDataChanged(() => {
			$(".popover").popover("hide");
		});

		this.dataTable.on("preDraw", () => {
			this.updateTimeColumnWidth(this.getCurrentPageRecords());
		});

		this.initCompleteDeferred = $.Deferred();
		this.dataTable.on("init.dt", () => {
			this.initCompleteDeferred.resolve();
		});

		this.contextMenu.registerOnTable(this.dataTable);
	}

	/**
	 * Adjusts the width of the {@code th.time-column} header based on the data currently visible on the page.
	 *
	 * This method is registered as a {@code preDraw} handler for ALL {@link BasicDataTable} subclasses, including
	 * those whose entries are not even Records (e.g. {@code ValidationResult}). The guards at the top
	 * of this method are therefore intentional and necessary:
	 *
	 * 1. If any record is missing {@code start} or {@code end} (i.e. {@code undefined}), the record type does not
	 *    carry time-range data at all — bail out immediately without touching the DOM.
	 * 2. If the table has no {@code th.time-column} header, there is nothing to resize — bail out.
	 */
	private updateTimeColumnWidth(records: T[]) {
		if (!records.every(hasTimeRangeFields)) {
			return;
		}

		const tableField = this.config.element.find("th.time-column");
		if (tableField.length === 0) {
			return;
		}

		const is24HourFormat = globals.timeFormat === "HH:mm";

		let timeZoneShown = globals.showTimeZone;
		let dateDifferent = false;
		let onlyDaysUnit = true;
		let daysUnitEntry = false;
		let changedEntry = false;
		let changedEntryDateDifferent = false;

		records.forEach(record => {
			const startDateTime = parseISOZonedDateTime(record.start);
			const endDateTime = parseISOZonedDateTime(record.end);

			if (startDateTime && endDateTime) {
				if (startDateTime.offset !== endDateTime.offset) {
					timeZoneShown = true;
				}
				if (!isSameDay(startDateTime, endDateTime)) {
					dateDifferent = true;
					if (changedEntryDateDifferent === false && !(record.editUnit === DurationUnit.Days || record.editUnit === DurationUnit.HalfDays)) {
						changedEntryDateDifferent = record.changed;
					}
				}
			}

			if (record.editUnit === DurationUnit.Days || record.editUnit === DurationUnit.HalfDays) {
				daysUnitEntry = true;
			} else {
				onlyDaysUnit = false;
			}

			if (changedEntry === false && !(record.editUnit === DurationUnit.Days || record.editUnit === DurationUnit.HalfDays)) {
				changedEntry = record.changed;
			}
		});

		let timeWidth = is24HourFormat
			? BasicDataTable.TIME_24_WIDTH
			: BasicDataTable.TIME_12_WIDTH;

		if (timeZoneShown) {
			timeWidth += BasicDataTable.TIMEZONE_FIELD_WIDTH;
		}

		const leftWidth = Math.max(timeWidth, BasicDataTable.MAX_DATE_WIDTH);

		if (onlyDaysUnit) {
			tableField.width(BasicDataTable.DAYSUNIT_FIELD_WIDTH);
		} else if (dateDifferent) {
			const rightWidth = Math.max(timeWidth, BasicDataTable.MAX_DATE_WIDTH);
			tableField.width(leftWidth + BasicDataTable.ARROW_WIDTH + rightWidth + (changedEntryDateDifferent ? (BasicDataTable.CHANGED_WIDTH) : 0));
		} else {
			const upperWidth = 2 * timeWidth + BasicDataTable.ARROW_WIDTH;

			const bottomWidth = daysUnitEntry
				? BasicDataTable.DAYSUNIT_FIELD_WIDTH
				: BasicDataTable.MAX_DATE_WIDTH;
			tableField.width(Math.max(upperWidth, bottomWidth) + (changedEntry ? BasicDataTable.CHANGED_WIDTH : 0));
		}
	}

	public filter(filterData: RequestParam[]): JQueryPromise<T[]> {
		this.filterData = filterData;
		return this.reload(true);
	}

	public reload(resetPaging: boolean = false): JQueryPromise<T[]> {
		if (typeof this.dataSource === "string") {
			// Datasource as an url -> default ajax loading
			(this.dataTable.settings()[0] as DataTables.SettingsLegacy).jqXHR?.abort();
			const deferred = $.Deferred();
			this.dataTable.ajax.reload(() => deferred.resolve(), resetPaging);
			return deferred.promise();
		} else if (Array.isArray(this.dataSource)) {
			// Datasource as an array -> load data from array again
			this.reloadOfflineData(this.dataSource, !resetPaging);
			return promiseNow(this.dataSource);
		} else if (typeof this.dataSource === "function") {
			// Datasource as a function that returns an array or a promise of an array
			const source = this.dataSource();

			if (Array.isArray(source)) {
				// Returned source is an array -> reload directly
				this.reloadOfflineData(source, !resetPaging);
				return promiseNow(source);
			} else if (isJQueryPromise<T[]>(source)) {
				// Returned source is a promise so add a hook to reload on done
				return source.done((data) => {
					this.reloadOfflineData(data, !resetPaging);
				});
			}
		}

		return promiseNow();
	}

	public getDataTable(): DataTables.Api {
		return this.dataTable;
	}

	// Note: this seems to reload the data from the datasource
	public redrawCurrentPage(invalidate: boolean = false) {
		if (invalidate) {
			this.dataTable.rows().invalidate();
		}
		this.dataTable.draw("page");
	}

	public getOrderParams(): JQuerySerializeArrayElement[] {
		const order = this.dataTable.order();
		const columns = this.getColumnSettings();
		const params: JQuerySerializeArrayElement[] = [];
		if (order) {
			const column: DataTables.ColumnSettings = columns[order[0][0] as number];
			const orderDir = order[0][1];
			// only send order params if column has a data property and orderDir is set
			if (column?.data && typeof column.data === "string" && orderDir) {
				params.push(
					{name: "orderColumn", value: column.data},
					{name: "orderDir", value: String(orderDir)}
				);
			}
		}
		return params;
	}

	protected abstract createDataTableConfig(): DataTables.Settings;

	protected abstract getColumnSettings(): TypedColumnSettings<T>[];

	protected abstract getDefaultOrder(): (string | number)[][];

	/** Per-row draw hook for subclasses. `row` is the DataTables `<tr>` Node */
	protected rowCallback(row: Node, data: T, index: number) {
	}

	protected createBasicConfig(): DataTables.Settings {
		const basicConfig: DataTables.Settings = {
			columns: this.getColumnSettings(),
			order: this.getDefaultOrder(),
			select: false,
			rowCallback: (row, data, index) => {
				this.rowCallback(row, data as T, index);
			},
			buttons: {
				buttons: [],
				dom: {
					container: {tag: null},
					buttonContainer: {tag: null},
					buttonLiner: {tag: null},
					button: {className: "btn btn-default heading-btn"}
				}
			}
		};

		if (typeof this.dataSource === "string") {
			return mergeProperties({
				serverSide: true,
				ajax: {
					url: this.dataSource,
					data: (data: DataTables.AjaxDataRequest & Record<string, unknown>) => {
						this.planifyAjaxData(data);
						this.extendAjaxData(data, this.filterData);
					}
				}
			}, basicConfig);
		} else if (Array.isArray(this.dataSource)) {
			return mergeProperties({
				serverSide: false,
				ajax: false,
				data: this.dataSource
			}, basicConfig);
		} else {
			return mergeProperties({
				serverSide: false,
				ajax: false,
				data: []
			}, basicConfig);
		}
	}

	/**
	 * Flattens each column's nested `search: {value, regex}` into the flat `searchValue`/`searchRegex`
	 * keys our Spring backend expects.
	 *
	 * The server binds request columns into `List<Map<DataTableColumnParameter, String>>` (keyed by the
	 * `DataTableColumnParameter` enum), which can only hold flat string values — it cannot bind DataTables'
	 * default nested `columns[i][search][value]` shape. Hence this reshape.
	 *
	 * Note: the backend only ever reads each column's `data` (to resolve the order column); `searchValue`/
	 * `searchRegex` are bound but unused, and table search is disabled (`searching: false`). This is kept for
	 * protocol compatibility — see {@link PlanifiedAjaxColumn}.
	 */
	protected planifyAjaxData(data: DataTables.AjaxDataRequest) {
		// Client-side (`serverSide: false`) tables issue no `columns` in the ajax request, unlike upstream's always-present typing.
		if (!data.columns) {
			return;
		}
		for (const column of data.columns as PlanifiedAjaxColumn[]) {
			column.searchRegex = column.search?.regex;
			column.searchValue = column.search?.value;
			delete column.search;
		}
	}

	protected extendAjaxData(data: Record<string, unknown>, elements: RequestParam[]): Record<string, unknown> {
		for (const element of elements) {
			data[element.name] = element.value;
		}
		return data;
	}

	public getAllRecords(): T[] {
		return this.dataTable.rows().data().toArray() as T[];
	}

	public getCurrentPageRecords(): T[] {
		return this.dataTable.rows({page: "current"}).data().toArray() as T[];
	}

	public getRowCount(): number {
		return this.dataTable.rows().data().length;
	}

	protected getRowData(trtd: JQuery): T {
		const tr = trtd.is("tr") ? trtd : trtd.closest("tr");
		return (this.dataTable.row(tr).data() || tr.data("recording")) as T;
	}

	public static getRowData<T>(dataTable: DataTables.Api, trtd: JQuery) {
		const tr = trtd.is("tr") ? trtd : trtd.closest("tr");
		return (dataTable.row(tr).data() || tr.data("recording")) as T;
	}

	/** Subscribe to the `dataChanged` event with the full server response as payload. */
	public onDataChanged<TResponse = unknown>(handler: (response: TResponse) => void): this {
		$(this).on(BasicDataTable.EVENT_DATA_CHANGED, (event, data: TResponse) => handler(data));
		return this;
	}

	// Rendering helpers

	protected static renderEmpty(this: void, data: unknown, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return "";
	}

	protected static renderText(this: void, data: string, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return htmlEscape(data);
	}

	protected static renderNumber(this: void, data: number, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return data != null ? htmlEscape(data.toString()) : "";
	}

	protected static renderPositionIcon(this: void, data: unknown, type: string, row: {
		startPosition?: GPSPosition;
		endPosition?: GPSPosition
	}, meta: DataTables.CellMetaSettings): string {
		if (row && (row.startPosition?.id || row.endPosition?.id)) {
			const i = $("<i>").addClass("far fa-crosshairs");
			const a = $("<a>").append(i);

			if (GPSPositionUtil.onlyDeletedPositionsAvailable(row)) {
				i.addClass("gps-position-deleted");
			}

			return a.prop("outerHTML") as string;
		} else {
			return "";
		}
	}

	protected static renderPlatformIcon(this: void, data: string | null, type: string, row: ReportRecord, meta: DataTables.CellMetaSettings, isStartPlatform: boolean): string {
		return data
			? $("<i>").addClass(`fal fa-platform-${data}`)
				.prop("title", buildPlatformIconTitle(data, row, isStartPlatform))
				.prop("outerHTML") as string
			: "";
	}

	/**
	 * Renders a date-time while keeping the timezone contained in the parsed value.
	 */
	protected static renderDateTime(this: void, data: string, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return data ? formatDateTime(parseISOZonedDateTime(data)) : "";
	}

	/**
	 * Renders a timestamp-like date-time in the browser time zone. The wire offset reflects the account
	 * zone, not the viewer's, so re-anchor to the browser zone for a viewer-local reading.
	 */
	protected static renderDateTimeLocal(this: void, data: string, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return data ? formatDateTime(parseISOZonedDateTime(data)?.withTimeZone(Temporal.Now.timeZoneId())) : "";
	}

	protected static renderDate(this: void, data: string, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return data ? formatDate(parseISODate(data)) : "";
	}

	protected static renderDateWithoutYear(this: void, data: string, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return data ? formatDateWithoutYear(parseISODate(data)) : "";
	}

	protected static renderDuration(this: void, data: number, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		if (type === "sort" || type === "type") {
			return String(data);
		}
		return formatDuration(data);
	}

	protected static renderHours(this: void, data: number, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return data != null ? formatDuration(data) : "";
	}

	protected static renderProjectTimeTaskPath = (data: string, type: string, row: ReportProjectTime, meta: DataTables.CellMetaSettings): string => {
		return this.renderTaskPathInternal(row.taskBreadCrumbs, row.taskName);
	}

	protected static renderRunningProjectTimeTaskPath = (data: string, type: string, row: RunningProjectTime, meta: DataTables.CellMetaSettings): string => {
		return this.renderTaskPathInternal(row.task.breadCrumbs, row.task.name);
	}

	private static renderTaskPathInternal(taskBreadCrumbs: string, taskName: string): string {
		const taskPathWithoutTask = breadcrumbsWithoutName(taskBreadCrumbs, taskName);

		return `<div>
					<div class="${taskPathWithoutTask !== "" ? "text-grey-600" : ""}">${htmlEscape(taskName)}</div>
					<div class="text-size-small">${taskPathWithoutTask}</div>
				</div>`;
	}

	protected static renderDateTimeWithTimeZone(this: void, data: unknown, type: string, row: {
		start: string;
		end?: string;
		startTimezone?: string;
		endTimezone?: string;
		changed: boolean;
		durationDays?: number;
	}, meta: DataTables.CellMetaSettings): string {
		const startDate = parseISOZonedDateTime(row.start);
		const changed = row.changed
			? `<div class="edited-pen"><i class="fal fa-pen" title="${messages["report.column.changed.title"]}"></i></div>` : "";
		if (row.end) {
			const endDate = parseISOZonedDateTime(row.end);
			const timeZoneStringStartDate = (globals.showTimeZone || (row.startTimezone !== row.endTimezone)) ? row.startTimezone : "";
			const timeZoneStringEndDate = (globals.showTimeZone || (row.startTimezone !== row.endTimezone)) ? row.endTimezone : "";

			if ("durationDays" in row && row.durationDays) {
				return `<div>
							${formatDateDay(startDate)}, <span class="text-size-small"> ${formatDate(startDate)}</span>
						</div>`;
			} else {
				if (formatDate(startDate) === formatDate(endDate)) {
					return `<div class="flexbox-container">
								<div>
									<span>${formatTime(startDate)}</span>
									<span class="text-timezone"> ${timeZoneStringStartDate}</span>
									<i class="time-separation-symbol"></i>
									<span> ${formatTime(endDate)}
										<span class="text-timezone"> ${timeZoneStringEndDate} </span>
									</span>
									<div>
										${formatDateDay(startDate)}, <span class="text-size-small">${formatDate(startDate)}</span>
									</div>
								</div>
								${changed}
							</div>
						`;
				} else {
					return `<div class="flexbox-container">
								<div>
									${formatTime(startDate)}<span class="text-timezone"> ${timeZoneStringStartDate}</span>
									<div>
										${formatDateDay(startDate)}, <span class="text-size-small">${formatDate(startDate)}</span>
									</div>
								</div>
								<div>
									<i class="time-separation-symbol"></i>
								</div>
								<div>
									${formatTime(endDate)}<span class="text-timezone"> ${timeZoneStringEndDate}</span>
									<div>
										${formatDateDay(endDate)}, <span class="text-size-small">${formatDate(endDate)}</span>
									</div>
								</div>
								${changed}
							</div>`;
				}
			}
		} else {
			const timeZoneStringRecordingStart = globals.showTimeZone ? startDate?.offset ?? "" : "";
			return `<div>
						<span>${formatTime(startDate)}</span>
						<span class="text-timezone"> ${timeZoneStringRecordingStart}</span>
						<div>
							${formatDateDay(startDate)}, <span class="text-size-small">${formatDate(startDate)}</span>
						</div>
					</div>`;
		}
	}

	protected static renderPausedAt(this: void, data: string, type: string, row: RunningEntry, meta: DataTables.CellMetaSettings): string {
		if (!row.pausedAt) {
			return "";
		}
		const start = parseISOZonedDateTime(row.start);
		const pausedAt = parseISOZonedDateTime(row.pausedAt);
		const timeZoneString = (globals.showTimeZone || pausedAt.offset !== start.offset) ? pausedAt.offset : "";
		const dateString = formatDate(start) !== formatDate(pausedAt) ? `<div>${formatDateDay(pausedAt)}, <span class="text-size-small">${formatDate(pausedAt)}</span></div>` : "";
		return `<div>
					<span>${formatTime(pausedAt)}</span>
					<span class="text-timezone"> ${timeZoneString}</span>
				</div>
				${dateString}`;
	}

	protected static renderSelectCheckbox(this: void, data: unknown, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		return `<div class="select-checkbox"></div>`;
	}

	protected static renderDescriptionText(this: void, data: string, type: string, row: unknown, meta: DataTables.CellMetaSettings): string {
		data = data || "";
		data = htmlEscape(data);
		data = data.replace(/\n/g, "<br/>");
		return `<div>${data}</div>`;
	}

	protected static renderUser(this: void, data: PartialUser | User): string {
		return data ? htmlEscape(data.fullname) : "";
	}

	protected createContextMenu(): DataTableContextMenu<T> {
		return new DataTableContextMenu();
	}

	public setScrollHeight(height: number) {
		this.config.element.parent(".dataTables_scrollBody")
			.css("max-height", `${height}px`);
	}

	public reloadOfflineData(data: T[], keepScrollPos: boolean = false) {
		let scrollBody: JQuery = null;
		let scrollPos: number = null;
		if (keepScrollPos) {
			scrollBody = this.config.element.parent("div.dataTables_scrollBody");
			scrollPos = scrollBody.scrollTop();
		}

		this.dataTable.clear().rows.add(data).draw();

		if (scrollBody != null) {
			scrollBody.scrollTop(scrollPos);
		}

		this.toggleLoadingIndicator(false);
	}

	public searchOfflineData(search: string) {
		this.dataTable.search(search).draw();
	}

	public getInitializationPromise(): JQueryPromise<void> {
		return this.initCompleteDeferred.promise();
	}

	public invalidateRows() {
		this.dataTable.rows().invalidate();
	}

	public toggleLoadingIndicator(toggle: boolean = true) {
		const indicator = this.config.element.parent(".dataTables_wrapper").find(".dataTables_processing");
		indicator.toggle(toggle);
	}
}
